import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { jobsService, driversService, notificationService } from "../../services";
import avatar from "../../assets/images/user-avatar.png";
import headerImage from "../../assets/images/header-image.png";

const IconSearch = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="7" />
    <line x1="16.5" y1="16.5" x2="21" y2="21" />
  </svg>
);

const IconBell = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 8-3 8h18s-3-1-3-8" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const IconLogout = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const IconSettings = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const IconMenu = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

const IconLoader = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    className="mq-search-spinner">
    <line x1="12" y1="2" x2="12" y2="6" />
    <line x1="12" y1="18" x2="12" y2="22" />
    <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
    <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
    <line x1="2" y1="12" x2="6" y2="12" />
    <line x1="18" y1="12" x2="22" y2="12" />
    <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
    <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
  </svg>
);

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

export default function Topbar({ onMenuClick, isMobile }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifLoading, setNotifLoading] = useState(false);
  const dropdownRef = useRef(null);
  const notifRef = useRef(null);
  const searchRef = useRef(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ jobs: [], drivers: [] });
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const debouncedQuery = useDebounce(searchQuery, 300);

  // Perform search
  useEffect(() => {
    const performSearch = async () => {
      if (debouncedQuery.trim().length < 2) {
        setSearchResults({ jobs: [], drivers: [] });
        setShowSearchDropdown(false);
        return;
      }
      setIsSearching(true);
      try {
        const [jobsRes, driversRes] = await Promise.allSettled([
          jobsService.getJobs({ search: debouncedQuery, limit: 5 }),
          driversService.getDrivers({ search: debouncedQuery, limit: 5 }),
        ]);
        setSearchResults({
          jobs: jobsRes.status === 'fulfilled' ? (jobsRes.value.jobs || []) : [],
          drivers: driversRes.status === 'fulfilled' ? (driversRes.value.drivers || []) : [],
        });
        setShowSearchDropdown(true);
      } catch {
        setSearchResults({ jobs: [], drivers: [] });
      } finally {
        setIsSearching(false);
      }
    };
    performSearch();
  }, [debouncedQuery]);

  // Fetch unread count on mount and poll every 30s
  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const count = await notificationService.getUnreadCount();
        setUnreadCount(count);
      } catch {
        // silently ignore
      }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fetch notifications when dropdown opens
  useEffect(() => {
    if (showNotifications) {
      const fetchNotifs = async () => {
        setNotifLoading(true);
        try {
          const data = await notificationService.getNotifications({ limit: 10 });
          setNotifications(data.notifications || []);
          setUnreadCount(data.unreadCount || 0);
        } catch {
          // silently ignore
        } finally {
          setNotifLoading(false);
        }
      };
      fetchNotifs();
    }
  }, [showNotifications]);

  const handleMarkAllRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    } catch {
      // silently ignore
    }
  };

  const handleNotifClick = async (notif) => {
    if (!notif.isRead) {
      notificationService.markAsRead(notif._id).catch(() => {});
      setUnreadCount(prev => Math.max(0, prev - 1));
      setNotifications(prev => prev.map(n => n._id === notif._id ? { ...n, isRead: true } : n));
    }
    // Navigate based on type
    setShowNotifications(false);
    if (notif.refModel === 'Job' && notif.refId) {
      navigate(`/jobs/${notif.refId}`);
    } else if (notif.refModel === 'User' && notif.refId) {
      navigate(`/drivers/${notif.refId}`);
    } else if (notif.refModel === 'Review') {
      navigate('/reviews');
    }
  };

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const seconds = Math.floor((now - date) / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const getUserDisplayName = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user?.firstName) {
      return user.firstName;
    }
    if (user?.email) {
      return user.email.split("@")[0];
    }
    return "Admin";
  };

  const getUserRole = () => {
    if (user?.role?.name) {
      return user.role.name;
    }
    if (typeof user?.role === "string") {
      return user.role;
    }
    return "Administrator";
  };

  return (
    <header className="mq-topbar">
      {/* Mobile hamburger menu */}
      {isMobile && (
        <button
          className="mq-hamburger-btn"
          onClick={onMenuClick}
          aria-label="Open menu"
        >
          <IconMenu />
        </button>
      )}

      {/* Left: Search bar */}
      <div className="mq-search-box" ref={searchRef}>
        <span className="mq-search-icon">
          {isSearching ? <IconLoader /> : <IconSearch />}
        </span>
        <input
          type="text"
          placeholder="Search jobs, drivers"
          className="mq-search-input"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            if (e.target.value.trim().length >= 2) setShowSearchDropdown(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setShowSearchDropdown(false);
          }}
          onFocus={() => {
            if (searchQuery.trim().length >= 2) setShowSearchDropdown(true);
          }}
        />

        {showSearchDropdown && (
          <div className="mq-search-dropdown">
            {isSearching ? (
              <div className="mq-search-loading"><IconLoader /><span>Searching...</span></div>
            ) : !searchResults.jobs.length && !searchResults.drivers.length ? (
              <div className="mq-search-empty">No results found for "{searchQuery}"</div>
            ) : (
              <>
                {searchResults.jobs.length > 0 && (
                  <div className="mq-search-section">
                    <div className="mq-search-section-header">Jobs</div>
                    {searchResults.jobs.map((job) => (
                      <div key={job._id} className="mq-search-result-item" onClick={() => {
                        setShowSearchDropdown(false);
                        setSearchQuery('');
                        navigate(`/jobs/${job._id}`);
                      }}>
                        <div className="mq-search-result-content">
                          <div className="mq-search-result-title">{job.jobId || job._id?.slice(-8)}</div>
                          <div className="mq-search-result-subtitle">
                            {job.from?.name || job.from?.address || 'Unknown'} → {job.to?.name || job.to?.address || 'Unknown'}
                          </div>
                        </div>
                        <span className="mq-search-result-badge" style={{
                          backgroundColor: job.status === 'delivered' ? '#10b981' : job.status === 'in-transit' ? '#8b5cf6' : '#f59e0b'
                        }}>{job.status}</span>
                      </div>
                    ))}
                  </div>
                )}
                {searchResults.drivers.length > 0 && (
                  <div className="mq-search-section">
                    <div className="mq-search-section-header">Drivers</div>
                    {searchResults.drivers.map((driver) => (
                      <div key={driver._id} className="mq-search-result-item" onClick={() => {
                        setShowSearchDropdown(false);
                        setSearchQuery('');
                        navigate(`/drivers/${driver._id}`);
                      }}>
                        <div className="mq-search-result-content">
                          <div className="mq-search-result-title">
                            {driver.firstName} {driver.lastName}
                          </div>
                          <div className="mq-search-result-subtitle">{driver.email || driver.phone}</div>
                        </div>
                        <span className="mq-search-result-badge" style={{
                          backgroundColor: driver.isActive ? '#10b981' : '#6b7280'
                        }}>{driver.isActive ? 'Active' : 'Inactive'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Right: User pill and bell */}
      <div className="mq-topbar-right">
        <div className="mq-user-section" ref={dropdownRef}>
          <div
            className="mq-user-pill"
            onClick={() => setShowDropdown(!showDropdown)}
            style={{ cursor: "pointer" }}
          >
            <img
              src={user?.avatar || avatar}
              alt={getUserDisplayName()}
              className="mq-user-avatar"
            />
            <span className="mq-user-name">{getUserDisplayName()}</span>
            <img
              src={headerImage}
              alt="Header Icon"
              className="mq-user-header-image"
            />
          </div>

          {/* Dropdown Menu */}
          {showDropdown && (
            <div className="mq-user-dropdown">
              <div className="mq-user-dropdown-header">
                <img
                  src={user?.avatar || avatar}
                  alt={getUserDisplayName()}
                  className="mq-dropdown-avatar"
                />
                <div className="mq-dropdown-user-info">
                  <span className="mq-dropdown-name">{getUserDisplayName()}</span>
                  <span className="mq-dropdown-role">{getUserRole()}</span>
                </div>
              </div>
              <div className="mq-user-dropdown-divider" />
              <button
                className="mq-dropdown-item"
                onClick={() => {
                  setShowDropdown(false);
                  navigate("/settings");
                }}
              >
                <IconSettings />
                <span>Settings</span>
              </button>
              <button className="mq-dropdown-item mq-dropdown-logout" onClick={handleLogout}>
                <IconLogout />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>

        <div className="mq-notif-section" ref={notifRef}>
          <button
            className="mq-bell-button"
            type="button"
            onClick={() => setShowNotifications(!showNotifications)}
          >
            <IconBell />
            {unreadCount > 0 && <span className="mq-bell-dot" />}
          </button>

          {showNotifications && (
            <div className="mq-notif-dropdown">
              <div className="mq-notif-dropdown-header">
                <span className="mq-notif-title">Notifications</span>
                {unreadCount > 0 && (
                  <button className="mq-notif-mark-all" onClick={handleMarkAllRead}>
                    Mark all read
                  </button>
                )}
              </div>
              <div className="mq-notif-list">
                {notifLoading ? (
                  <div className="mq-notif-empty">
                    <span>Loading...</span>
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="mq-notif-empty">
                    <IconBell />
                    <span>No notifications yet</span>
                  </div>
                ) : (
                  notifications.map((notif) => (
                    <div
                      key={notif._id}
                      className={`mq-notif-item ${!notif.isRead ? 'mq-notif-unread' : ''}`}
                      onClick={() => handleNotifClick(notif)}
                    >
                      <div className="mq-notif-item-content">
                        <div className="mq-notif-item-title">{notif.title}</div>
                        <div className="mq-notif-item-message">{notif.message}</div>
                        <div className="mq-notif-item-time">{formatTimeAgo(notif.createdAt)}</div>
                      </div>
                      {!notif.isRead && <div className="mq-notif-unread-dot" />}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
